import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Home, Info, Phone, Briefcase, Calculator, CalendarDays } from 'lucide-react';
import { Button } from './ui/button';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Home', icon: Home },
    { path: '/services', label: 'Services', icon: Briefcase },
    { path: '/calculator', label: 'Calculator', icon: Calculator },
    { path: '/book', label: 'Book', icon: CalendarDays },
    { path: '/about', label: 'About', icon: Info },
    { path: '/contact', label: 'Contact', icon: Phone },
  ];

  return (
    <>
      {/* Top Logo Banner - Big and Bold */}
      <div className="bg-[#6B7280] text-white" data-testid="logo-banner">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <Link to="/" className="block py-6 md:py-8 text-center group">
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-block"
            >
              {/* Top Decorative Line - Full Width */}
              <div className="w-full mb-3">
                <div className="h-[1px] w-full bg-white/40 mx-auto" style={{ maxWidth: '600px' }}></div>
              </div>
              
              {/* Main Logo Text */}
              <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl font-light tracking-[0.15em] text-white/90 group-hover:text-white transition-colors duration-300 px-4" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
                LOANS <span className="italic text-white/70">&</span> BEYOND
              </h1>
              
              {/* Bottom Decorative Line - Full Width */}
              <div className="w-full mt-3">
                <div className="h-[1px] w-full bg-white/40 mx-auto" style={{ maxWidth: '600px' }}></div>
              </div>
            </motion.div>
          </Link>
        </div>
      </div>

      {/* Navigation Bar */}
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.5, delay: 0.2 }}
        className={`sticky top-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'bg-white/95 backdrop-blur-xl shadow-soft'
            : 'bg-white border-b border-border'
        }`}
        data-testid="navbar"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <nav className="flex items-center justify-between h-14 md:h-16">
            {/* Mobile Logo (shows when scrolled on mobile) */}
            <Link to="/" className="md:hidden font-serif text-[#6B7280] text-lg tracking-wider" style={{ fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
              L<span className="italic text-[#9CA3AF]">&</span>B
            </Link>

            {/* Desktop Navigation - Centered */}
            <div className="hidden md:flex items-center justify-center flex-1 gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={`px-4 py-2 rounded-full font-medium text-sm transition-all duration-300 ${
                    location.pathname === link.path
                      ? 'bg-[#6B7280] text-white'
                      : 'text-gray-500 hover:text-[#6B7280] hover:bg-gray-100'
                  }`}
                  data-testid={`nav-link-${link.label.toLowerCase()}`}
                >
                  {link.label}
                </Link>
              ))}
            </div>

            {/* CTA Button */}
            <div className="hidden md:block">
              <Link to="/contact">
                <Button 
                  className="bg-[#6B7280] hover:bg-[#4B5563] text-white rounded-full px-6 py-5 font-medium transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg text-sm"
                  data-testid="nav-cta-button"
                >
                  Free Quote
                </Button>
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="md:hidden p-2 text-[#6B7280] hover:bg-gray-100 rounded-lg transition-colors"
              data-testid="mobile-menu-toggle"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </nav>
        </div>

        {/* Mobile Menu */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="md:hidden bg-white border-t border-border overflow-hidden"
              data-testid="mobile-menu"
            >
              <div className="px-6 py-4 space-y-1">
                {navLinks.map((link) => {
                  const Icon = link.icon;
                  return (
                    <Link
                      key={link.path}
                      to={link.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      className={`flex items-center gap-3 py-3 px-4 rounded-xl font-medium transition-all ${
                        location.pathname === link.path
                          ? 'bg-[#6B7280] text-white'
                          : 'text-gray-500 hover:bg-gray-100 hover:text-[#6B7280]'
                      }`}
                      data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                    >
                      <Icon size={20} />
                      {link.label}
                    </Link>
                  );
                })}
                <Link to="/contact" onClick={() => setIsMobileMenuOpen(false)}>
                  <Button className="w-full bg-[#6B7280] hover:bg-[#4B5563] text-white rounded-full py-5 font-medium mt-4">
                    Get Free Quote
                  </Button>
                </Link>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.header>
    </>
  );
};
